import React, { useState } from "react";
import { useLoader } from "../../Common/GeneralLoader/GeneralLoaderProvider";
import { getCategoryListApi, getSubCategoryListApi } from "../../../api/api";
import { drupalSettings } from "../../../utils/config";
import { getCurrencySymbol } from "../../../utils/utils";

/** Minimum transfer amount by currency (must match backend NautAdminBankTransferConfirmationResource). */
export const MIN_TRANSFER_AMOUNT_BY_CURRENCY = {
  AUD: 2, BDT: 200, CNY: 0, HKD: 15, EUR: 1, USD: 2,
  IDR: 30000, INR: 180, JPY: 300, MYR: 7, NPR: 290, NZD: 3,
  PKR: 0, PHP: 115, SGD: 2, KRW: 2800, LKR: 600, THB: 60,
  TRY: 80, VND: 52000, CZK: 40, DKK: 12, HUF: 630, NOK: 19,
  PLN: 7, RON: 8, SEK: 18, GBP: 1, CAD: 2, ARS: 2800,
  BRL: 10, COP: 7500, BOB: 13, CLP: 1700, MXN: 34, PEN: 6,
  UYU: 76, EGP: 95, MAD: 18, ILS: 6, AED: 7,
};

const sortByTitle = (list) =>
  [...list].sort((a, b) => {
    const nameA = (a.title || "").toString().trim().toLowerCase();
    const nameB = (b.title || "").toString().trim().toLowerCase();
    return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
  });

export const NewTransfer = ({ setStep, payload, setPayload }) => {
  const [error, setError] = useState(false);
  const { showLoader, hideLoader } = useLoader(); // Destructure isLoading, showLoader, and hideLoader

  const formErrors = {
    recipient_required: Drupal.t("Recipient field is required."),
    reference_required: Drupal.t("Reference field is required."),
    charter_required: Drupal.t("Charter field is required."),
    category_required: Drupal.t("Category field is required."),
    subcategory_required: Drupal.t("Subcategory field is required."),
    store_recipient_details_required: Drupal.t("Store Recipient Details field is required."),
    amount_required: Drupal.t("Amount field is required."),
    amount_below_minimum: (min, currency) => Drupal.t("Amount cannot be less than @currency @min.", { "@min": min, "@currency": getCurrencySymbol(currency) }),
    technical_error:
      Drupal.t("Sorry, there was an error. Please try again later or reach out to support@nautzero.com."),
  };

  // State for form inputs
  const [recipient, setRecipient] = useState(payload?.flow === "stored" ? payload?.recipientDetails?.recipient_name : recipient);
  const [recipientError, setRecipientError] = useState("");
  const [reference, setReference] = useState(payload?.reference || "");
  const [referenceError, setReferenceError] = useState("");
  const [category, setCategory] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [subcategory, setSubCategory] = useState("");
  const [subcategoryError, setSubCategoryError] = useState("");
  const [charter, setCharter] = useState("");
  const [charterError, setCharterError] = useState("");
  const [file, setFile] = useState("");
  const [notes, setNotes] = useState(payload?.notes || "");
  const [storeRecipientDetails, setStoreRecipientDetails] = useState(false);
  const [storeRecipientDetailsError, setStoreRecipientDetailsError] = useState("");
  const [amount, setAmount] = useState(payload?.amount || null);
  const [amountError, setAmountError] = useState("");

  const [categoryList, setCategoryList] = useState([]);
  const [charterList, setCharterList] = useState([]);
  const [subCategoryList, setSubCategoryList] = useState([]);


  const handleSubmit = (event) => {
    event.preventDefault();

    let isValid = true; // Flag to track if all validations pass

    if (!recipient) {
      setRecipientError(formErrors?.recipient_required);
      isValid = false;
    }
    else {
      setRecipientError("");
    }

    if (!reference) {
      setReferenceError(formErrors?.reference_required);
      isValid = false;
    }
    else {
      setReferenceError("");
    }

    // if (!charter) {
    //   setCharterError(formErrors?.charter_required);
    //   isValid = false;
    // }
    // else {
    //   setCharterError("");
    // }

    if (!category) {
      setCategoryError(formErrors?.category_required);
      isValid = false;
    }
    else {
      setCategoryError("");
    }

    if (!subcategory) {
      setSubCategoryError(formErrors?.subcategory_required);
      isValid = false;
    }
    else {
      setSubCategoryError("");
    }

    if (payload?.flow === "new" && !storeRecipientDetails && storeRecipientDetails !== 0) {
      setStoreRecipientDetailsError(formErrors?.store_recipient_details_required);
      isValid = false;
    }
    else {
      setStoreRecipientDetailsError("");
    }

    console.log("payload", payload);

    const accountCurrency = payload?.recipientDetails?.payload?.beneficiary?.bank_details?.account_currency;
    const minAmount = accountCurrency != null
      ? (MIN_TRANSFER_AMOUNT_BY_CURRENCY[accountCurrency] ?? 0)
      : 0;
    const amountNum = parseFloat(String(amount || "").replace(/,/g, ""), 10);

    if (!amount && amount !== 0) {
      setAmountError(formErrors?.amount_required);
      isValid = false;
    }
    else if (Number.isNaN(amountNum) || amountNum < minAmount) {
      setAmountError(formErrors?.amount_below_minimum(minAmount, accountCurrency));
      isValid = false;
    }
    else {
      setAmountError("");
    }

    // If any validation failed, prevent form submission
    if (!isValid) {
      return;
    }

    setError(false);

    setPayload({
      ...payload,
      recipient,
      reference,
      category,
      subcategory,
      charter,
      file,
      notes,
      storeRecipientDetails,
      amount,
      beneficiaryFormData: payload?.recipientDetails?.payload || {},
    }); // Update payload with the form data

    setStep(4.1); // Skip to confirm details step for stored recipient
  };


  // Function to fetch user info
  const fetchCategoryList = async () => {
    showLoader();
    try {
      const response = await getCategoryListApi({ yatchId: drupalSettings?.user?.yacht_id });
      setCategoryList(Object.keys(response?.category).map((item, index) => { return { id: item, title: response?.category?.[item] }}) || []);
      setCharterList(Object.keys(response?.charter).map((item, index) => { return { id: item, title: response?.charter?.[item] }}) || []);
    } catch (err) {
      console.log("Failed to fetch locations info");
    } finally {
      hideLoader();
    }
  };

  // Function to fetch user info
  const fetchSubCategoryList = async () => {
    showLoader();
    try {
      const response = await getSubCategoryListApi({ yatchId: drupalSettings?.user?.yacht_id, categoryId: category });
      setSubCategoryList(Object.keys(response?.category).map((item, index) => { return { id: item, title: response?.category?.[item] }}) || []);
    } catch (err) {
      console.log("Failed to fetch locations info");
    } finally {
      hideLoader();
    }
  };

  // Fetch info when the component mounts
  React.useEffect(() => {
    fetchCategoryList();
  }, []);

  React.useEffect(() => {
    setSubCategory("");
    console.log("Category changed:", category);
    if (category) {
      fetchSubCategoryList();
    }
    else {
      setSubCategoryList([]);
    }
  }, [category]);

  React.useEffect(() => {
    jQuery('[name="subcategory"]').trigger('change.select2');
  }, [subcategory])

  React.useEffect(() => {
    jQuery('.create-new-transfer-form .select2').on('change', function (e) {
      const value = jQuery(this).val();

      if (jQuery(this).attr('name') === 'category') {
        setCategory(value);
      }
      else if (jQuery(this).attr('name') === 'subcategory') {
        setSubCategory(value);
      }
      else if (jQuery(this).attr('name') === 'charter') {
        setCharter(value);
      }
      else if (jQuery(this).attr('name') === 'store_recipient_details') {
        setStoreRecipientDetails(value);
      }
    });

    jQuery('input[type="file"]').on("change", function (e) {
      e.stopPropagation();
      const fileLabel = jQuery(this).siblings(".file-placeholder")

      if (this.files && this.files.length > 0) {
        jQuery(fileLabel).html(this.files[0].name); // show file name
      } else {
        jQuery(fileLabel).html("Upload"); // reset placeholder
      }

      setFile(e.target.files[0]); // set file state
    });

    jQuery('.file-placeholder').on("click", function (e) {
      e.stopPropagation();
      jQuery(this).siblings('input[type="file"]').trigger('click');
    });

    var inputMaskConfig = {
      alias: "numeric",
      groupSeparator: ",",
      autoGroup: true,
      digits: 2,
      radixPoint: ".",
      allowMinus: false,
      prefix: getCurrencySymbol(payload?.recipientDetails?.payload?.beneficiary?.bank_details?.account_currency) + " ",
      rightAlign: false,
      removeMaskOnSubmit: true,
      autoUnmask: true,
      showMaskOnHover: false,
      placeholder: "",
    };
    if (drupalSettings.min) {
      inputMaskConfig.min = drupalSettings.min;
    }
    Inputmask("decimal", inputMaskConfig).mask('[name="amount"]');
  }, []);


  React.useEffect(() => {
    // Category
    if (payload?.category) {
      jQuery('.create-new-transfer-form select[name="category"]')
        .val(payload.category)
        .trigger('change');
      setCategory(payload.category);
    }

    // Subcategory
    if (payload?.subcategory) {
      jQuery('.create-new-transfer-form select[name="subcategory"]')
        .val(payload.subcategory)
        .trigger('change');
      setSubCategory(payload.subcategory);
    }

    // Charter
    if (payload?.charter) {
      jQuery('.create-new-transfer-form select[name="charter"]')
        .val(payload.charter)
        .trigger('change');
      setCharter(payload.charter);
    }

    // Store recipient details
    if (payload?.flow === "new" && (payload?.storeRecipientDetails === 0 || payload?.storeRecipientDetails === 1)) {
      jQuery('.create-new-transfer-form select[name="store_recipient_details"]')
        .val(payload.storeRecipientDetails)
        .trigger('change');
      setStoreRecipientDetails(payload.storeRecipientDetails);
    }
  }, [payload, categoryList, subCategoryList, charterList]);

  return (
    <>
      <div className="create-transfer-header">
        <h4 className="create-transfer-title">Create a New <span className="yellow">Transfer</span></h4>
      </div>
      <div className="create-transfer-body-content">
        <form className="create-new-transfer-form">
					<div className="form--item form--group">
            <label className="form--label">Recipient's Name*</label>
            <input
              type="text"
              name="recipient-name"
              className="form_control"
              id="recipient-name"
              aria-required="true"
              placeholder="Name"
              required
              onChange={(e) => setRecipient(e.target.value)}
              value={payload?.flow === "stored" ? payload?.recipientDetails?.recipient_name : recipient}
              readOnly={payload?.flow === "stored" && payload?.recipientDetails?.recipient_name}
            />
             {recipientError && (
                <div className="popup-has-error">
                  <div className="error_messages">{recipientError}</div>
                </div>
              )}
          </div>
          <div className="form--item form--group">
            <label className="form--label">Reference Number*</label>
            <input type="text" name="recipient-number" className="form_control" aria-required="true" placeholder="XXXXXXXX" required defaultValue={payload?.reference || ""} value={reference} onChange={(e) => setReference(e.target.value.trim())}/>
            {referenceError && (
              <div className="popup-has-error">
                <div className="error_messages">{referenceError}</div>
              </div>
            )}
          </div>
          <div className="form--group category--row">
            <div className="form--item form--group">
              <label className="form--label">Amount*</label>
              <input type="text" name="amount" className="form_control" aria-required="true" placeholder={getCurrencySymbol(payload?.recipientDetails?.payload?.beneficiary?.bank_details?.account_currency) + " 0.00"} required  defaultValue={payload?.amount || ""} onChange={(e) => setAmount(e.target.value)}/>
              {amountError && (
                <div className="popup-has-error">
                  <div className="error_messages">{amountError}</div>
                </div>
              )}
            </div>
          </div>
          <div className="form--group category--row">
            <div className="form--item half--col">
              <label className="form--label">Category*</label>
              <select className="form-select select2" name="category" value={category}>
                <option value="">Select</option>
                {sortByTitle(categoryList).map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.title}
                  </option>
                ))}
              </select>
              {categoryError && (
                <div className="popup-has-error">
                  <div className="error_messages">{categoryError}</div>
                </div>
              )}
            </div>
            <div className="form--item half--col">
              <label className="form--label">Subcategory*</label>
              <select className="form-select select2" name="subcategory" value={subcategory}>
                <option value="">Select</option>
                {sortByTitle(subCategoryList).map((subcategory) => (
                  <option key={subcategory.id} value={subcategory.id}>
                    {subcategory.title}
                  </option>
                ))}
              </select>
              {subcategoryError && (
                <div className="popup-has-error">
                  <div className="error_messages">{subcategoryError}</div>
                </div>
              )}
            </div>
          </div>
          <div className="form--group category--row">
            <div className="form--item half--col">
            <label className="form--label">Charter</label>
            <select className="form-select select2" name="charter">
              <option value="">Select</option>
              {sortByTitle(charterList).map((charter) => (
                <option key={charter.id} value={charter.id}>
                  {charter.title}
                </option>
              ))}
            </select>
            {charterError && (
              <div className="popup-has-error">
                <div className="error_messages">{charterError}</div>
              </div>
            )}
            </div>
            <div className="half--col"></div>
          </div>
          <div className="form--item form--group">
            <label className="form--label">Copy of Invoice</label>
            <div className="file-field-wrapper">
              <input type="file" id="receipt-upload--file" name="files[receipt]" size="22" className="form_control form-file" onChange={(e) => setFile(e.target.files[0])} />
              <span className="file-placeholder">Upload</span>
            </div>
          </div>
          <div className="form--item form--group">
            <label className="form--label">Add Notes</label>
            <textarea id="notes" name="notes" className="form_control" rows="4"  defaultValue={payload?.notes || ""} onChange={(e) => setNotes(e.target.value)}></textarea>
          </div>
          {
            payload?.flow === "new" ? <div className="form--group category--row">
              <div className="form--item half--col">
                <label className="form--label">Store Recipient Details?*</label>
                <select className="form-select select2" name="store_recipient_details">
                  <option value="">Select</option>
                  <option key={1} value={1}>Yes</option>
                  <option key={0} value={0}>No</option>
                </select>
                {storeRecipientDetailsError && (
                  <div className="popup-has-error">
                    <div className="error_messages">{storeRecipientDetailsError}</div>
                  </div>
                )}
              </div>
              <div className="half--col"></div>
            </div> : null
          }
					<div className="create-transfer-actions">
            <button className="secondary__btn" type="submit" onClick={() => setStep(2.1)}>Go Back</button>
            <button className="primary__btn" type="submit" onClick={handleSubmit}>Next</button>
          </div>
				</form>
      </div>
    </>
  );
};
